import { scaleColor, type LinearRGB } from '../core/color';
import type { Vec2 } from '../geo/types';
import { mixColor, waterReflectance } from './waterCues';

/**
 * Water under a roof (STYLE §7, free views): the Dam's sluice passage is some 67 m of water under a
 * vault. The free curve of waterCues.ts mirrors the clear sky, which is right everywhere else and
 * wrong here, where no sky stands over the water: rowed through, the passage showed bright, sky-lit
 * water under dark masonry. Under a cover the water is its body in the vault's shade, mirroring the
 * vault instead of the sky by the same reflectance, and the daylight of the open water reaches
 * `mouthM` in from either mouth.
 *
 * `bodyShade` and `vaultShade`, the share of the daylight on the water and on the masonry over it,
 * and `mouthM` are estimates judged on the rendered passage, not measured values.
 */
export const WATER_COVER = { slots: 2, bodyShade: 0.4, vaultShade: 0.3, mouthM: 8 } as const;

/** A stretch of covered water: its middle line in layout coordinates, from one mouth to the other. */
export interface WaterCover {
  readonly from: Vec2;
  readonly to: Vec2;
  readonly halfWidthM: number;
  /** The roof's own colour in daylight; the water mirrors it in its shade. */
  readonly roof: LinearRGB;
}

/** How far a place on the water is under a cover: 0 in the open, 1 past the mouths' daylight. */
export function coverAt(p: Vec2, cover: WaterCover): number {
  const [dx, dy] = [cover.to[0] - cover.from[0], cover.to[1] - cover.from[1]];
  const length = Math.hypot(dx, dy);
  if (length === 0) return 0;
  const [ax, ay] = [dx / length, dy / length];
  const [mx, my] = [(cover.from[0] + cover.to[0]) / 2, (cover.from[1] + cover.to[1]) / 2];
  const along = length / 2 - Math.abs((p[0] - mx) * ax + (p[1] - my) * ay);
  const across = cover.halfWidthM - Math.abs((p[1] - my) * ax - (p[0] - mx) * ay);
  if (across <= 0 || along <= 0) return 0;
  const t = Math.min(1, along / WATER_COVER.mouthM);
  return t * t * (3 - 2 * t);
}

/** The colour a cover's roof shows in the water under it. */
export function coverMirror(cover: WaterCover): LinearRGB {
  return scaleColor(cover.roof, WATER_COVER.vaultShade);
}

/** The albedo of water lying `covered` under a cover, from what it would be in the open. */
export function coveredWaterAlbedo(
  lit: LinearRGB,
  body: LinearRGB,
  grazingDeg: number,
  cover: WaterCover,
  covered: number
): LinearRGB {
  if (covered <= 0) return lit;
  const shaded = mixColor(
    scaleColor(body, WATER_COVER.bodyShade),
    coverMirror(cover),
    waterReflectance(grazingDeg)
  );
  return mixColor(lit, shaded, covered);
}
